/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListChatSourcesRequest,
  ListChatSourcesSecurity,
} from "../models/operations/listchatsources.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListChatSourcesQuery,
  ListChatSourcesQueryData,
  prefetchListChatSources,
  queryKeyListChatSources,
} from "./listChatSources.core.js";
export {
  buildListChatSourcesQuery,
  type ListChatSourcesQueryData,
  prefetchListChatSources,
  queryKeyListChatSources,
};

export type ListChatSourcesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSources chat
 *
 * @remarks
 * List the distinct agent sources present in this project's chats, for populating the agent-type filter on the Agent Sessions page.
 */
export function useListChatSources(
  request?: ListChatSourcesRequest | undefined,
  security?: ListChatSourcesSecurity | undefined,
  options?: QueryHookOptions<
    ListChatSourcesQueryData,
    ListChatSourcesQueryError
  >,
): UseQueryResult<ListChatSourcesQueryData, ListChatSourcesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListChatSourcesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSources chat
 *
 * @remarks
 * List the distinct agent sources present in this project's chats, for populating the agent-type filter on the Agent Sessions page.
 */
export function useListChatSourcesSuspense(
  request?: ListChatSourcesRequest | undefined,
  security?: ListChatSourcesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListChatSourcesQueryData,
    ListChatSourcesQueryError
  >,
): UseSuspenseQueryResult<ListChatSourcesQueryData, ListChatSourcesQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListChatSourcesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListChatSourcesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramChatSession?: string | undefined;
    },
  ],
  data: ListChatSourcesQueryData,
): ListChatSourcesQueryData | undefined {
  const key = queryKeyListChatSources(...queryKeyBase);

  return client.setQueryData<ListChatSourcesQueryData>(key, data);
}

export function invalidateListChatSources(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
      gramChatSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "chat", "listSources", ...queryKeyBase],
  });
}

export function invalidateAllListChatSources(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "chat", "listSources"],
  });
}
